/**
 * FractionGrading.js — PURE, dependency-free grading logic for the `tiles`
 * fraction-builder widget (Task #268).
 *
 * This module is imported by the CLIENT widget (FractionWidgets.buildTiles) AND
 * mirrored EXACTLY by the SERVER (brilliant_courses._grade_tiles). Keeping the
 * client/server grade logic identical is the whole point of factoring it out:
 *   - it has NO DOM, NO audio, NO imports, so it runs under plain Node and is
 *     trivially unit-testable;
 *   - all arithmetic is INTEGER-PAIR cross-multiplication (never floats), so a
 *     fraction's value is exact and compares identically in JS and Python.
 *
 * A `tiles` widget is a structured fraction EQUATION:
 *   fractions: [ { num: CELL, den: CELL }, ... ]
 * where CELL is one of:
 *   - a number / {fixed:N}          → a printed constant (no blank)
 *   - {slot:true}                   → an empty blank the learner fills
 *   - {base:N, op:"mul"|"div", slot:true} → "N × □" or "N ÷ □" (one blank)
 *
 * The learner fills the blanks in CANONICAL ORDER: each fraction left→right,
 * numerator BEFORE denominator. `value` (what check() returns / the server
 * grades) is the array of filled blank integers in that exact order.
 *
 * Grading modes (spec.grade):
 *   - "slots" (default): value must deep-equal spec.answer OR any spec.answerAlts
 *     entry. Use for every "make this written equation true" case where the
 *     intended numbers matter (read-the-shape, simpler form, ×n / ÷n).
 *   - "value" (alias "equivalent"): every fraction's VALUE must be equal (and
 *     equal spec.target when given); spec.different:true additionally forbids
 *     re-writing the target literally. Accepts ANY equivalent form (2/4 == 1/2).
 *     Use for open-ended "write a fraction = X" / "write a DIFFERENT fraction = X".
 *   - "asWritten": every fraction's COMPUTED (num,den) pair must be LITERALLY
 *     identical (so 2/4 does NOT equal 1/2), and equal spec.target as written
 *     when given. Use when the exact written form matters, not just the value.
 */

function _gcd(a, b) {
  a = Math.abs(a); b = Math.abs(b);
  while (b) { [a, b] = [b, a % b]; }
  return a || 1;
}

export function parseFrac(v) {
  if (v && typeof v === "object" && v.den != null) {
    return { num: Math.trunc(Number(v.num) || 0), den: Math.trunc(Number(v.den) || 1) || 1 };
  }
  const s = String(v == null ? "" : v).trim();
  const m = s.match(/^(-?\d+)\s*\/\s*(-?\d+)$/);
  if (m) return { num: parseInt(m[1], 10), den: parseInt(m[2], 10) || 1 };
  const n = parseInt(s, 10);
  return { num: isNaN(n) ? 0 : n, den: 1 };
}

// Normalize one authored cell to { kind, ... }. kind ∈ "fixed" | "slot" | "op".
export function normCell(c) {
  if (c == null) return { kind: "fixed", value: 0 };
  if (typeof c === "number") return { kind: "fixed", value: Math.trunc(c) };
  if (typeof c === "string") {
    const n = parseInt(c, 10);
    return { kind: "fixed", value: isNaN(n) ? 0 : n };
  }
  if (c.slot) {
    if (c.base != null && (c.op === "mul" || c.op === "div")) {
      return { kind: "op", base: Math.trunc(Number(c.base) || 0), op: c.op };
    }
    return { kind: "slot" };
  }
  if (c.fixed != null) return { kind: "fixed", value: Math.trunc(Number(c.fixed) || 0) };
  return { kind: "fixed", value: 0 };
}

function _hasSlot(cell) {
  const k = normCell(cell).kind;
  return k === "slot" || k === "op";
}

// Total number of fill blanks, counted in canonical order. A term is either a
// fraction ({num,den}) or a whole number ({whole: CELL}); a whole contributes at
// most one blank.
export function slotCount(fractions) {
  let n = 0;
  (fractions || []).forEach((f) => {
    if (!f || typeof f !== "object") return;
    if (f.whole !== undefined) { if (_hasSlot(f.whole)) n++; return; }
    if (_hasSlot(f.num)) n++;
    if (_hasSlot(f.den)) n++;
  });
  return n;
}

function _isInt(v) {
  return typeof v === "number" && Number.isFinite(v) && Math.floor(v) === v;
}

// Evaluate a cell to an integer-pair rational [p, q] (q != 0), pulling the next
// filled value when the cell has a blank. Returns null on invalid input.
function _evalCell(cell, pull) {
  const nc = normCell(cell);
  if (nc.kind === "fixed") return [nc.value, 1];
  const s = pull();
  if (!_isInt(s)) return null;
  if (nc.kind === "slot") return [s, 1];
  // op
  if (nc.op === "mul") return [nc.base * s, 1];
  if (s === 0) return null;          // ÷ by zero
  return [nc.base, s];               // base ÷ s  → rational base/s
}

function _fractionRational(f, pull) {
  const num = _evalCell(f && f.num, pull);
  const den = _evalCell(f && f.den, pull);
  if (!num || !den) return null;
  const P = num[0] * den[1];
  const Q = num[1] * den[0];
  if (Q === 0) return null;
  return [P, Q];
}

// Evaluate one equation TERM to an integer-pair rational. A term is either a
// fraction ({num,den}) or a bare whole number ({whole: CELL}).
function _termRational(f, pull) {
  if (f && typeof f === "object" && f.whole !== undefined) return _evalCell(f.whole, pull);
  return _fractionRational(f, pull);
}

function _ratMul(a, b) { return [a[0] * b[0], a[1] * b[1]]; }
function _ratAdd(a, b) { return [a[0] * b[1] + b[0] * a[1], a[1] * b[1]]; }

// Split a term list into "=" groups, keeping each group's inner +/× operators.
// ops[k] is the operator BETWEEN terms[k] and terms[k+1]: "eq" | "add" | "mul".
function _splitGroups(terms, ops) {
  const groups = [];
  let curT = [terms[0]], curO = [];
  for (let k = 0; k < ops.length; k++) {
    const nxt = terms[k + 1];
    if (ops[k] === "eq") { groups.push({ terms: curT, ops: curO }); curT = [nxt]; curO = []; }
    else { curT.push(nxt); curO.push(ops[k] === "mul" ? "mul" : "add"); }
  }
  groups.push({ terms: curT, ops: curO });
  return groups;
}

// Evaluate one group's expression to a rational, × binding tighter than +.
function _combine(terms, ops) {
  const vals = [terms[0].slice()];
  for (let k = 0; k < ops.length; k++) {
    if (ops[k] === "mul") vals[vals.length - 1] = _ratMul(vals[vals.length - 1], terms[k + 1]);
    else vals.push(terms[k + 1].slice());
  }
  let acc = vals[0];
  for (let k = 1; k < vals.length; k++) acc = _ratAdd(acc, vals[k]);
  return acc;
}

function _ratEqual(a, b) { return a[0] * b[1] === b[0] * a[1]; }

// Literal integer-pair equality (sign-normalized): [2,4] is NOT equal to [1,2].
function _ratIdentical(a, b) {
  let [p, q] = a, [r, s] = b;
  if (q < 0) { p = -p; q = -q; }
  if (s < 0) { r = -r; s = -s; }
  return p === r && q === s;
}

function _arrEq(a, b) {
  return Array.isArray(a) && Array.isArray(b) && a.length === b.length &&
    a.every((x, i) => _isInt(Number(x)) && Math.trunc(Number(x)) === b[i]);
}

/**
 * gradeTiles(spec, value) -> boolean. The single source of truth for tiles
 * correctness (mirrored byte-for-byte in Python).
 */
export function gradeTiles(spec, value) {
  const fractions = (spec && spec.fractions) || [];
  const n = slotCount(fractions);
  if (!Array.isArray(value) || n === 0 || value.length !== n) return false;
  if (!value.every(_isInt)) return false;

  const g = spec && spec.grade;
  const mode = (g === "value" || g === "equivalent") ? "value"
    : g === "asWritten" ? "asWritten"
    : (g === "sum" || g === "product" || g === "expr") ? "expr" : "slots";

  if (mode === "slots") {
    const cands = [];
    if (Array.isArray(spec.answer)) cands.push(spec.answer);
    if (Array.isArray(spec.answerAlts)) {
      spec.answerAlts.forEach((a) => { if (Array.isArray(a)) cands.push(a); });
    }
    return cands.some((a) => _arrEq(a, value));
  }

  // expr: evaluate the whole equation (terms combined by +/×, × before +) and
  // require every "="-separated group to be rationally equal — and equal to
  // spec.target when given. Accepts ANY valid decomposition/factoring, so a
  // missing-addend or missing-factor problem grades every correct combination.
  if (mode === "expr") {
    let ei = 0;
    const epull = () => value[ei++];
    const terms = fractions.map((f) => _termRational(f, epull));
    if (terms.some((t) => t == null)) return false;
    const ops = Array.isArray(spec.ops) ? spec.ops : [];
    const groups = _splitGroups(terms, ops);
    const vals = groups.map((grp) => _combine(grp.terms, grp.ops));
    if (vals.some((v) => v == null || v[1] === 0)) return false;
    for (let k = 1; k < vals.length; k++) if (!_ratEqual(vals[0], vals[k])) return false;
    if (spec.target != null) {
      const t = parseFrac(spec.target);
      if (!_ratEqual(vals[0], [t.num, t.den])) return false;
    } else if (groups.length < 2) {
      return false; // nothing to satisfy (no "=" and no target)
    }
    return true;
  }

  // value / asWritten both evaluate every fraction to an integer-pair rational.
  let i = 0;
  const pull = () => value[i++];
  const rats = fractions.map((f) => _fractionRational(f, pull));
  if (rats.some((r) => r == null)) return false;

  if (mode === "asWritten") {
    for (let k = 1; k < rats.length; k++) if (!_ratIdentical(rats[0], rats[k])) return false;
    if (spec.target != null) {
      const t = parseFrac(spec.target);
      if (!_ratIdentical(rats[0], [t.num, t.den])) return false;
    }
    return true;
  }

  // value (formerly "equivalent")
  for (let k = 1; k < rats.length; k++) if (!_ratEqual(rats[0], rats[k])) return false;

  if (spec.target != null) {
    const t = parseFrac(spec.target);
    if (!_ratEqual(rats[0], [t.num, t.den])) return false;
  }
  if (spec.different && spec.target != null) {
    let j = 0;
    const pull2 = () => value[j++];
    const num0 = _evalCell(fractions[0] && fractions[0].num, pull2);
    const den0 = _evalCell(fractions[0] && fractions[0].den, pull2);
    const t = parseFrac(spec.target);
    if (num0 && den0 && num0[1] === 1 && den0[1] === 1 &&
        num0[0] === t.num && den0[0] === t.den) {
      return false;
    }
  }
  return true;
}

// Reduce an integer-pair to lowest terms (exported for tidy reveal labels).
export function reduceFrac(num, den) {
  if (!den) return { num, den };
  const g = _gcd(num, den);
  return { num: num / g, den: den / g };
}

// ── Area grading (shade / overlapGrid) ──────────────────────────────────────
// Area widgets are graded by EXACT rational arithmetic too, so a skill check
// never depends on a float area comparison. Skill-check area widgets therefore
// author EITHER explicit `regions` each with a rational `area`, OR a uniform
// figure (grid rows×cols / parts / den) whose cells are all 1/N.

// Parse an authored area into an integer-pair rational, or null if it isn't one.
function _areaRat(a) {
  if (a == null) return null;
  if (typeof a === "object" && a.den != null) {
    const n = Math.trunc(Number(a.num) || 0), d = Math.trunc(Number(a.den) || 0);
    return d ? [n, d] : null;
  }
  if (typeof a === "string") { const f = parseFrac(a); return f.den ? [f.num, f.den] : null; }
  if (typeof a === "number") {
    if (Number.isInteger(a)) return [a, 1];
    for (let d = 2; d <= 1000; d++) {
      const n = Math.round(a * d);
      if (Math.abs(n / d - a) < 1e-9) return [n, d];
    }
    return null;
  }
  return null;
}

// Resolve a widget spec to the ordered list of its region areas as rationals,
// or null when it can't be graded exactly.
export function areaRationals(spec) {
  if (!spec || typeof spec !== "object") return null;
  if (Array.isArray(spec.regions) && spec.regions.length) {
    const out = [];
    for (const r of spec.regions) {
      const rat = _areaRat(r && r.area);
      if (!rat) return null;
      out.push(rat);
    }
    return out;
  }
  let n = null;
  const grid = spec.grid;
  if (grid && typeof grid === "object") {
    const rc = (parseInt(grid.rows, 10) || 0) * (parseInt(grid.cols, 10) || 0);
    if (rc > 0) n = rc;
  }
  if (n == null && spec.rows && spec.cols) {
    const rc = (parseInt(spec.rows, 10) || 0) * (parseInt(spec.cols, 10) || 0);
    if (rc > 0) n = rc;
  }
  if (n == null && spec.parts) n = parseInt(spec.parts, 10) || null;
  if (n == null && spec.den) n = parseInt(spec.den, 10) || null;
  if (n && n > 0) { const out = []; for (let i = 0; i < n; i++) out.push([1, n]); return out; }
  return null;
}

// True when the selected cell indices form a solid filled rectangle on an
// rows×cols grid (row-major indices). Used for "shade the a×c block" products.
export function isRectangleSelection(indices, rows, cols) {
  const idx = (indices || []).map((x) => Math.trunc(Number(x))).filter(Number.isFinite);
  if (!idx.length || !(cols > 0)) return false;
  const set = new Set(idx);
  let minr = Infinity, maxr = -Infinity, minc = Infinity, maxc = -Infinity;
  for (const i of idx) {
    const r = Math.floor(i / cols), c = i % cols;
    if (r < minr) minr = r; if (r > maxr) maxr = r;
    if (c < minc) minc = c; if (c > maxc) maxc = c;
  }
  const expected = (maxr - minr + 1) * (maxc - minc + 1);
  if (expected !== set.size) return false;
  for (let r = minr; r <= maxr; r++)
    for (let c = minc; c <= maxc; c++)
      if (!set.has(r * cols + c)) return false;
  return true;
}

// gradeArea(spec, indices) -> boolean. Sums the authored areas at the selected
// indices and checks the total equals spec.target EXACTLY. Optional requireRect
// (overlapGrid × mode) additionally demands a solid rectangular block.
export function gradeArea(spec, indices) {
  const areas = areaRationals(spec);
  if (!areas || !Array.isArray(indices)) return false;
  const seen = new Set();
  let acc = [0, 1];
  for (const raw of indices) {
    const i = Math.trunc(Number(raw));
    if (!Number.isFinite(i) || i < 0 || i >= areas.length || seen.has(i)) return false;
    seen.add(i);
    acc = _ratAdd(acc, areas[i]);
  }
  if (spec.target == null) return false;
  const t = parseFrac(spec.target);
  if (!t.den || !_ratEqual(acc, [t.num, t.den])) return false;
  if (spec.requireRect) {
    const rows = parseInt((spec.grid && spec.grid.rows) || spec.rows, 10);
    const cols = parseInt((spec.grid && spec.grid.cols) || spec.cols, 10);
    if (rows > 0 && cols > 0 && !isRectangleSelection(indices, rows, cols)) return false;
  }
  return true;
}

// compareWinner(spec) -> index of the winning option (greatest / smallest), or
// -1. Values come from each option's num/den (abstract cards) or its shaded
// region areas. Mirrored server-side so a compare skill-check is authoritative.
export function optionValue(opt) {
  if (!opt || typeof opt !== "object") return 0;
  if (Array.isArray(opt.regions) && opt.regions.length) {
    const shaded = new Set((opt.shaded || []).map(Number));
    let n = 0, d = 1;
    opt.regions.forEach((r, i) => {
      if (!shaded.has(i)) return;
      const a = _areaRat(r && r.area);
      if (a) { n = n * a[1] + a[0] * d; d = d * a[1]; }
    });
    return d ? n / d : 0;
  }
  const den = Math.max(1, parseInt(opt.den, 10) || 1);
  const num = parseInt(opt.num, 10) || 0;
  return den ? num / den : 0;
}

export function compareWinner(spec) {
  const opts = _compareOptions(spec);
  if (!opts.length) return -1;
  const ask = spec && spec.ask === "smaller" ? "smaller" : "bigger";
  let best = 0;
  for (let i = 1; i < opts.length; i++) {
    const v = optionValue(opts[i]), b = optionValue(opts[best]);
    if (ask === "bigger" ? v > b : v < b) best = i;
  }
  return best;
}

// Normalize a compare spec to an options array (supports legacy left/right).
export function _compareOptions(spec) {
  if (spec && Array.isArray(spec.options) && spec.options.length) return spec.options;
  if (spec && spec.left != null && spec.right != null) {
    return [spec.left, spec.right].map((f) => (typeof f === "object" ? f : parseFrac(f)));
  }
  return [];
}
